import {Injectable} from "@angular/core";
import {ActivatedRouteSnapshot, NavigationEnd, Router, RouterEvent, RouterStateSnapshot} from "@angular/router";
import {HttpClient} from "@angular/common/http";
import {BehaviorSubject, Observable} from "rxjs";
import {BackendService} from "./backend.service";
import {Cart, CartService} from "./cart.service";
import {filter} from "rxjs/operators";
import {CheckoutSteps, OrderMethod, TableType} from "./constants";
import {Host} from "./models/host";
import {Menu} from "./models/menu";
import {Table} from "./models/table";
import {timeDelta} from "./util";

// declare the global window so typescript doesn't complain
declare var window: {
  [key: string]: any;
  prototype: Window;
  new(): Window;
};


export function getStepRoute(step: CheckoutSteps): Array<string> {
  switch (step) {
    case CheckoutSteps.start:
      return ['/', 'checkout', 'start'];
    case CheckoutSteps.review:
      return ['/', 'checkout'];
    case CheckoutSteps.chooseMethod:
      return ['/', 'checkout', 'method'];
    case CheckoutSteps.enterContact:
      return ['/', 'checkout', 'contact'];
    case CheckoutSteps.choosePayment:
      return ['/', 'checkout', 'payment'];
    case CheckoutSteps.chooseTime:
      return ['/', 'checkout', 'time']
    case CheckoutSteps.selectBenefit:
      return ['/', 'checkout', 'select-benefit']
    case CheckoutSteps.submitDemo:
      return ['/', 'demo', 'success']
    case CheckoutSteps.submitOrder:
      // this must be handled manually
      return []
  }
}

/**
 * the app configuration consists of host, menu and table
 */
interface AppConfig {
  host: Host,
  menu: Menu,
  table: Table,
  tables: Array<Table>,
  cart: Cart
}


/**
 * AppService keeps global state and functions which should be accessible from anywhere
 */
@Injectable({providedIn: 'root'})
export class AppService {

  in_frame: boolean = false;
  in_popup: boolean = false;
  embedded: boolean = false;

  idtag: string;
  menu: Menu = null;
  host: Host = null;
  showOnly: boolean = false;
  table: Table = null;
  tables: Array<Table> = new Array<Table>();
  cart: Cart = null;

  // BehaviourSubject emits the current value right on subscription, and new values
  // each time we call this.subject.next(). So we can send a new menu whenever we
  // want, e.g. when table is changed or (later) when we get a new status.
  // https://rxjs.dev/guide/subject#behaviorsubject
  appConfigSubject = new BehaviorSubject<AppConfig>({host: null, menu: null, table: null, cart: null, tables: null});
  loadingSubject = new BehaviorSubject<boolean>(true);

  load(): Observable<AppConfig> {
    // just return the subject, so subscribers can observe it. A subject is
    // always an Observable, too.
    return this.appConfigSubject;
  }

  constructor(
    private router: Router,
    private http: HttpClient,
    private cartService: CartService,
    private backendService: BackendService
  ) {
    // are we running in frame? https://stackoverflow.com/a/33799884
    this.in_frame = !(window === window.parent);
    this.in_popup = (window.opener && window.opener !== window);
    this.embedded = this.in_popup || this.in_frame;
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: RouterEvent) => {
        this.trackPageview(event.url);
      }
    )
  }

  setTable(idtag: string): void {
    // try to load the config
    if (idtag && this.idtag != idtag) {
      this.idtag = idtag;
      this.backendService.setIdTag(idtag);
      this.backendService.getT4DData().toPromise().then(data => {
        this.menu = Menu.load(data);
        this.host = new Host(data.host, this.menu);
        this.table = new Table(data.table);
        this.tables = data.tables.map(t=>new Table(t));
        // fix group/item references in

        this.cartService.getCartFromStorage(this.menu, this.host, this.table).then((cart) => {
          this.cart = cart;
          // emit new config
          this.appConfigSubject.next({
            host: this.host, menu: this.menu, table: this.table, cart: this.cart, tables: this.tables
          })
          this.loadingSubject.next(false);
          this.injectMatomo();
        });
      }).catch((error) => {
        // error: set empty config
        console.error(error);
        this.appConfigSubject.next({host: null, menu: null, table: null, cart: null, tables: null});
        this.loadingSubject.next(false);
      })

    }
  }

  getLoading(): Observable<boolean> {
    return this.loadingSubject;
  }

  setLoading(loading: boolean): void {
    this.loadingSubject.next(loading);
  }

  canClose(): boolean {
    return Boolean(this.in_popup || this.in_frame || this.host.domain);
  }

  closeApp() {
    if (this.in_frame) {
      window.top.postMessage('close', '*');
    } else if (this.in_popup) {
      window.close();
    } else {
      window.location.href = this.host.return_page;
    }
  }

  /**
   * return true if we have an active table which takes orders
   */
  hasActiveTable(): boolean {
    if (!this.table) {
      return false;
    }
    if (this.table.type == TableType.inhouse) {
      return this.table.is_active;
    } else {
      return this.host.has_delivery || this.host.has_takeout;
    }
  }

  /**
   * return true if we can take orders (e.g. active table, and opening hours)
   */
  getOrdersEnabled(): boolean {
    if (this.host && this.table) {
      // do we have an active table?
      if (!this.hasActiveTable()) {
        return false;
      }
      // is ordering allowed?
      if (!this.host.allow_order || this.showOnly) {
        return false;
      }
      // check opening hours
      const oh = this.host.getOpeningHours();
      if (this.table.type == TableType.inhouse) {
        return oh.isOpen;
      } else {
        // ordering is possible if the next possible date is less than preorder_days away
        const nextDate = this.host.getEarliestDate(this.cart.method, this.cart.address.city),
              days = Math.floor(timeDelta(nextDate) / (60*60*24));
        return days <= this.host.preorder_days;
      }
    } else {
      return false;
    }
  }

  checkDiscountCode(code: string) {
    this.setLoading(true);
    return this.backendService.checkCode(code).toPromise()
      .then(() => {

        }
      ).finally(()=>{
        this.setLoading(false);
      })
  }

  sendServiceCall() {
    this.cartService.sendServiceCall(this.table).toPromise().then( () => {
      this.router.navigate(['/', 'service', 'success']);
    })
  }

  /**
   * inject matomo
   */
  injectMatomo() {
    if (!this.host.matomo_id) {
      return;
    }
    window._paq = window?._paq || [];
    if (window._paq.length == 0) {
      window._paq.push(['disableCookies']);
      window._paq.push(['trackPageView']);
      window._paq.push(['enableLinkTracking']);
      window._paq.push(['setTrackerUrl', 'https://stats.greybyte.com/matomo.php']);
      window._paq.push(['setSiteId', this.host.matomo_id]);
    }
    (() => {
      const u = '';
      const d = document;
      const g = d.createElement('script');
      const s = d.getElementsByTagName('script')[0];
      g.type = 'text/javascript';
      g.async = true;
      g.defer = true;
      g.src = 'https://stats.greybyte.com/matomo.js';
      s.parentNode.insertBefore(g, s);
    })();
  }

  /**
   * track pageview in Matomo
   */
  trackPageview(url, customTitle?: string) {
    if (!window._paq || !this.host?.matomo_id) {
      return;
    }
    window._paq.push(['setCustomUrl', url]);
    if (customTitle) {
      window._paq.push(['setDocumentTitle', customTitle]);
    }
    window._paq.push(['trackPageView']);
  }

  /**
   * track event in matomo
   * @param category
   * @param action
   * @param name
   * @param value
   */
  trackEvent(category: string, action: string, name?: string, value?: number) {
    const args: any[] = [category, action];
    if (!window._paq || !this.host?.matomo_id) {
      return;
    }
    if (!!name) {
      args.push(name);
    }
    if (typeof value === 'number') {
      args.push(value);
    }
    window._paq.push(['trackEvent', ...args]);
  }

}
